<template>
	<div class="profile" v-if="!isNativeDevice" @mouseenter="handleMouseEnter" @mouseleave="handleMouseLeave">
		<img v-if="user_image" :src="url + user_image" ref="profileImage" alt="User" @click="toggleMenu" />
		<div class="user-avatar" v-else @click="toggleMenu">
			{{ user_name ? user_name.charAt(0).toUpperCase() : "" }}
		</div>
		<Menu
			ref="menu"
			:model="menuItems"
			:popup="true"
			class="profile-menu"
			:pt="{ root: { class: 'profile-menu-root', onMouseenter: onMenuMouseEnter, onMouseleave: onMenuMouseLeave } }"
		></Menu>
	</div>
	<div v-else class="profile">
		<img v-focus v-if="user_image" :src="url + user_image" ref="profileImage" alt="User" @click="toggleMenu" />
		<div v-focus class="user-avatar" v-else @click="toggleMenu">
			{{ user_name ? user_name.charAt(0).toUpperCase() : "" }}
		</div>
		<Menu
			ref="menu"
			:model="menuItems"
			:popup="true"
			class="profile-menu"
			:pt="{ root: { class: 'profile-menu-root' } }"
		></Menu>
	</div>
</template>

<script lang="ts" setup>
	import { computed, ref, nextTick, onMounted } from "vue"
	import { useUserStore } from "../stores/user"
	import { url } from "../helper"
	import { isNativeDevice } from "../helper"
	import router from "../router/router"
	import { useLogout } from "../composables/useLogout"
	import Menu from "primevue/menu"
	import { useI18n } from "vue-i18n"
	const { t } = useI18n()
	const { logout } = useLogout(router)
	const userStore = useUserStore()

	const user_image = computed(() => userStore.state.selected_profile?.field_profile_photo?.field_media_image?.uri?.url)
	const user_name = computed(() => userStore.state.selected_profile?.field_name || userStore.state.user.display_name)

	const menu = ref()
	const profileImage = ref()
	const isMenuHovered = ref(false)
	const isProfileHovered = ref(false)

	const menuItems = ref([
		{
			label: `${t("profile.greeting")}, ${user_name.value}`,
			visible: user_name.value !== undefined,
		},
		{
			visible: user_name.value !== undefined,
			separator: true,
		},
		{
			label: t("devices.title"),
			command: () => {
				router.push({ name: "DeviceManagement" })
			},
		},
		{
			label: `${t("profile.myProfile")}`,
			command: () => goToProfile(),
		},
		{
			label: `${t("profile.myAccount")}`,
			command: () => {
				window.location.href = `${url}/user`
			},
		},
		{
			separator: true,
		},
		{
			label: `${t("profile.logout")}`,
			command: () => userLogout(),
		},
	])

	const handleMouseEnter = async () => {
		isProfileHovered.value = true
		if (profileImage.value) {
			menu.value?.show(event, profileImage.value)
			await nextTick()
			centerMenu()
			attachMenuListeners()
		}
	}

	const handleMouseLeave = () => {
		isProfileHovered.value = false
		// Delay hiding to allow moving to menu
		setTimeout(() => {
			if (!isMenuHovered.value && !isProfileHovered.value) {
				menu.value?.hide()
			}
		}, 100)
	}

	const onMenuMouseEnter = () => {
		isMenuHovered.value = true
	}

	const onMenuMouseLeave = () => {
		isMenuHovered.value = false
		// Hide menu if not hovering profile either
		setTimeout(() => {
			if (!isMenuHovered.value && !isProfileHovered.value) {
				menu.value?.hide()
			}
		}, 100)
	}

	const attachMenuListeners = () => {
		const menuElement = document.querySelector(".profile-menu-root")
		if (menuElement) {
			menuElement.addEventListener("mouseenter", onMenuMouseEnter)
			menuElement.addEventListener("mouseleave", onMenuMouseLeave)
		}
	}
	const enableTvMenuNavigation = (menuEl: HTMLElement) => {
		const items = Array.from(menuEl.querySelectorAll<HTMLElement>(".p-menu-item-link"))

		items.forEach((item, index) => {
			item.setAttribute("tabindex", "0")

			item.addEventListener("keydown", (e: KeyboardEvent) => {
				switch (e.key) {
					case "ArrowDown":
						e.preventDefault()
						items[index + 1]?.focus()
						break
					case "ArrowUp":
						e.preventDefault()
						items[index - 1]?.focus()
						break
					case "Enter":
					case " ":
						e.preventDefault()
						item.click()
						break
				}
			})
		})
	}

	onMounted(async () => {
		await nextTick()

		const menuEl = menu.value?.$el
		if (menuEl && isNativeDevice) {
			enableTvMenuNavigation(menuEl)

			// Auto-focus first item for TV
			const firstItem = menuEl.querySelector(".p-menuitem-link")
			firstItem?.focus()
		}
	})

	const toggleMenu = async (event: Event) => {
		menu.value?.toggle(event)
		await nextTick()
		centerMenu()
		attachMenuListeners()
	}

	const centerMenu = () => {
		const menuElement = document.querySelector(".profile-menu-root")
		if (menuElement && profileImage.value) {
			const imgRect = profileImage.value.getBoundingClientRect()
			const menuRect = menuElement.getBoundingClientRect()
			const centerOffset = imgRect.left + imgRect.width / 2 - menuRect.width / 2
			;(menuElement as HTMLElement).style.left = `${centerOffset}px`
		}
	}

	const userLogout = () => {
		logout()
	}

	const goToProfile = () => {
		router.push("/profile")
	}
</script>

<style lang="scss" scoped>
	.profile {
		position: relative;
		cursor: pointer;

		img {
			transition: box-shadow 0.3s ease;
		}

		&:hover {
			border-radius: 50%;
			img {
				box-shadow: 0 0 12px rgba(0, 0, 0, 0.8);
			}
		}

		img {
			border-radius: 50%;
			width: 50px;
			height: 50px;
			object-fit: cover;
			object-position: center;
			background: var(--gray);

			@include br.respond-to(br.$laptop-large) {
				width: 45px;
				height: 45px;
			}

			@include br.tablet-down {
				width: 40px;
				height: 40px;
			}

			@include br.respond-to(br.$mobile-xlarge) {
				width: 30px;
				height: 30px;
			}
		}
	}

	.menu-header {
		padding: 10px;
		display: flex;
		justify-content: center;
		border-bottom: 1px solid rgba(255, 255, 255, 0.1);
		margin-bottom: 8px;

		.menu-avatar {
			border-radius: 50%;
			width: 60px;
			height: 60px;
			object-fit: cover;
			object-position: center;
			background: var(--gray);

			@include br.tablet-down {
				width: 40px;
				height: 40px;
			}
		}
	}
</style>

<style lang="scss">
	.profile-menu,
	.profile-menu-root {
		background: #000000 !important;
		border: 1px solid rgba(255, 255, 255, 0.1) !important;
		border-radius: 8px !important;
		box-shadow: none !important;
		// min-width: 100px;
		margin-top: 8px;

		@include br.respond-to(br.$mobile-xlarge) {
			right: 0;
			left: unset !important;
		}

		.p-menu-list {
			padding: 8px !important;
		}

		.p-menuitem {
			.p-menuitem-content {
				background: transparent !important;
				color: #ffffff !important;
				border-radius: 4px !important;

				&:hover {
					background: #ffffff !important;

					.p-menuitem-link {
						color: #000000 !important;
					}
				}

				.p-menuitem-link {
					color: #ffffff !important;
					padding: 10px 12px !important;

					.p-menuitem-text {
						color: inherit !important;
					}
				}
			}

			&.p-focus > .p-menuitem-content {
				background: #ffffff !important;

				.p-menuitem-link {
					color: #000000 !important;
				}
			}
		}

		.p-menuitem-separator {
			border-top: 1px solid rgba(255, 255, 255, 0.1) !important;
			margin: 4px 0 !important;
		}
	}

	.user-avatar {
		width: 50px;
		height: 50px;
		border-radius: 50%;
		background: var(--gray);
		color: var(--white);
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 24px;
		font-weight: bold;

		@include br.tablet-down {
			width: 40px;
			height: 40px;
			font-size: 18px;
		}
	}
</style>
